import { Controller } from "@hotwired/stimulus"
import { Tooltip } from "bootstrap"
import { usePersistedState } from "../mixins/persistable"

/**
 * Desktop main menu collapse — JS toggles state + collapsed-only Bootstrap tooltips.
 * Visuals (width, icons-only, dividers, BC hide) are CSS:
 * `body.layout-application.mainmenu--collapsed` in layout-application.scss.
 *
 * Tooltip titles come from already-rendered (i18n) label spans, or
 * `data-mainmenu-tooltip` for brand / explicit overrides.
 */
export default class extends Controller {
  static targets = ["toggle"]
  static values = {
    collapseLabel: { type: String, default: "Collapse menu" },
    expandLabel: { type: String, default: "Expand menu" },
  }

  connect() {
    this._iconTooltips = []
    this.state = usePersistedState(this, "mainmenu", { collapsed: false })
    this.collapsed = Boolean(this.state.get("collapsed"))
    this._apply()

    this.state.subscribe("collapsed", (value) => {
      this.collapsed = Boolean(value)
      this._apply()
    })

    this._syncBcHistory()
    this._boundRestore = this._onTurboRestore.bind(this)
    document.addEventListener("turbo:restore", this._boundRestore)
  }

  disconnect() {
    document.removeEventListener("turbo:restore", this._boundRestore)
    this._disposeIconTooltips()
  }

  /**
   * Keep browser history / Turbo snapshots aligned with session BC selection
   * (Turbo Stream BC switch does not change the URL by itself).
   */
  _syncBcHistory() {
    const bc = this.element.dataset.activeBcId
    if (bc == null || bc === "") return
    try {
      sessionStorage.setItem("tshActiveBcId", bc)
    } catch (_e) { /* private mode */ }

    const url = new URL(window.location.href)
    if (bc === "all") {
      if (url.searchParams.has("bc")) {
        url.searchParams.delete("bc")
        history.replaceState(history.state, "", url)
      }
    } else if (url.searchParams.get("bc") !== String(bc)) {
      url.searchParams.set("bc", String(bc))
      history.replaceState(history.state, "", url)
    }
  }

  _onTurboRestore() {
    let intended
    try {
      intended = sessionStorage.getItem("tshActiveBcId")
    } catch (_e) {
      return
    }
    if (!intended) return

    const menu = document.getElementById("mainmenu") || document.getElementById("mainmenu_mobile")
    if (!menu) return
    if (String(menu.dataset.activeBcId || "") === String(intended)) return

    const path = intended === "all" ? "/?bc=all" : `/?bc=${encodeURIComponent(intended)}`
    fetch(path, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.text() : null))
      .then((html) => {
        if (html && window.Turbo?.renderStreamMessage) {
          window.Turbo.renderStreamMessage(html)
        }
      })
      .catch(() => { /* non-fatal */ })
  }

  toggle(event) {
    event?.preventDefault()
    this.collapsed = !this.collapsed
    this.state.set("collapsed", this.collapsed)
    this._apply()
    window.phCapture?.("mainmenu_toggled", {
      feature: "layout",
      action: "toggled",
      collapsed: this.collapsed,
    })
  }

  _apply() {
    // Body class drives all CSS (incl. #content padding after Turbo replaces #mainmenu).
    document.body.classList.toggle("mainmenu--collapsed", this.collapsed)
    this.element.classList.toggle("mainmenu--collapsed", this.collapsed)

    if (this.hasToggleTarget) {
      const label = this.collapsed ? this.expandLabelValue : this.collapseLabelValue
      this.toggleTarget.setAttribute("aria-expanded", String(!this.collapsed))
      this.toggleTarget.setAttribute("aria-label", label)
      this.toggleTarget.setAttribute("title", label)
    }

    this._syncIconTooltips()
  }

  _syncIconTooltips() {
    this._disposeIconTooltips()
    if (!this.collapsed) return

    this._tooltipElements().forEach((el) => {
      const label = this._tooltipLabel(el)
      if (!label) return

      // Bootstrap allows only one component instance per element. BC selector and
      // user menu already have bs.dropdown — use native title for those.
      if (el.matches('[data-bs-toggle="dropdown"]')) {
        el.setAttribute("title", label)
        this._iconTooltips.push({ tip: null, el, usedNativeTitle: true })
        return
      }

      // Prefer title option at create time (getOrCreateInstance ignores options if instance exists).
      Tooltip.getInstance(el)?.dispose()

      const tip = Tooltip.getOrCreateInstance(el, {
        title: label,
        placement: "right",
        trigger: "hover focus",
        container: "body",
        customClass: "mainmenu-icon-tooltip",
      })
      this._iconTooltips.push({ tip, el })
    })
  }

  _disposeIconTooltips() {
    ;(this._iconTooltips || []).forEach(({ tip, el, usedNativeTitle }) => {
      if (usedNativeTitle) {
        el?.removeAttribute?.("title")
        return
      }
      try {
        tip?.dispose()
      } catch (_e) {
        // Element may already be detached (Turbo replace) — safe to ignore.
      }
      el?.removeAttribute?.("data-bs-original-title")
      el?.removeAttribute?.("aria-describedby")
    })
    this._iconTooltips = []
  }

  _tooltipElements() {
    // Visible collapsed icons only — skip hidden BC subtree links.
    return this.element.querySelectorAll(`
      .mainmenu-brand-link,
      .mainmenu-bc-toggle,
      ul.nav > li:not(.mainmenu-bc-section) a.nav-link,
      .dropdown.position-sticky > .dropdown-toggle
    `)
  }

  _tooltipLabel(el) {
    const explicit = el.getAttribute("data-mainmenu-tooltip")
    if (explicit?.trim()) return explicit.trim()

    for (const span of el.querySelectorAll(":scope > span")) {
      if (span.classList.contains("badge")) continue
      if (span.querySelector("i, .bi, [class^='bi-'], [class^='ti-']")) continue
      const text = span.textContent.replace(/\s+/g, " ").trim()
      if (text) return text
    }

    // Nested truncate (e.g. BC selector)
    const truncate = el.querySelector(":scope > .text-truncate, :scope .text-truncate")
    if (truncate) {
      const text = truncate.textContent.replace(/\s+/g, " ").trim()
      if (text) return text
    }

    return ""
  }
}
